<template>
  <div
    class="kd-text-layer"
    :style="parentStyle"
    v-tooltip="{
      content: tooltipText,
      show: showTooltip,
      trigger: 'manual',
      placement: 'left',
    }"
    :disabled="false"
    @click="onWrapperClick"
  >
    <!-- <div class="kd-text-layer__wrapper"> -->
      <!-- "textinput" event needed for IE11 only -->
      <div class="kd-text-layer__text"
        contenteditable
        @input="textEdited($event)"
        @textinput="textEdited($event)"
        @paste="onPaste($event)"
        spellcheck="false"
        v-html="text"
        ref="text"
        :style="{
          fontSize: fontSizeCss,
          fontFamily: fontFamilyCss,
          fontWeight: fontWeight,
          fontStyle: fontStyle,
          textAlign: textAlignCss,
          lineHeight: lineHeightCss,
          color: color,
        }">
      </div>
    <!-- </div> -->
  </div>
</template>

<script>
import * as log from 'loglevel';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { debounce } from 'debounce';
import directions from '@/shared/directions';
import browserCheck from '@/services/browser-check';

import tooltipTexts from '@/store/tooltip-texts';

import {
  ACTIVE_LAYER_ID,
  TOOLTIPS_VISIBLE,
  LAYER_IS_FIRST_OF_TYPE,
} from '@/store/getter-types';

import {
  SET_FONT_SIZE_FOR_ACTIVE_LAYER,
  SET_TEXT_FOR_ACTIVE_LAYER,
  SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT,
} from '@/store/mutation-types';

import {
  ACTIVATE_LAYER_BY_ID,
} from '@/store/action-types';

/*
 * TODO: - Spell-checking might be needed, there could be an option for it
 */

export default {
  props: {
    layer: Object,
  },
  data() {
    return {
      text: '',
      fontSize: 20,
      fontName: '', // TODO: Make sure the templates all have a fontName set
      fontWeight: 400,
      fontStyle: 'normal',
      textAlignmentX: directions.LEFT, // TODO: This must match the Templatedesigner default
      textAlignmentY: directions.TOP, // TODO: This must match the Templatedesigner default
      lineHeight: 1.2, // TODO: This must match the templatedesigner default
      color: '#000',
      textIsOverflowing: false,
    };
  },
  computed: {
    ...mapGetters([
      ACTIVE_LAYER_ID,
      TOOLTIPS_VISIBLE,
      LAYER_IS_FIRST_OF_TYPE,
    ]),
    layerIsActive() {
      return this.layer && this.layer.id === this.ACTIVE_LAYER_ID;
    },
    // visibilityCss() { return this.textIsOverflowing ? 'hidden' : 'block'},
    storedText() { return this.layer.text; },
    storedFontSize() { return this.layer.fontSize; },
    storedFontName() {
      if (this.layer && this.layer.fontName) {
        return this.layer.fontName;
      }
      return 'sans-serif';
    },
    storedFontWeight() { return this.layer.fontWeight; },
    storedFontStyle() { return this.layer.fontStyle; },
    storedColor() { return this.layer.color; },
    // TODO: Move default to shared layer-standards file
    storedAlignmentX() { return this.layer.alignmentX || directions.LEFT; },
    // TODO: Move default to shared layer-standards file
    storedAlignmentY() { return this.layer.alignmentY || directions.TOP; },
    storedLineHeight() { return this.layer.lineHeight || 1.2; }, // TODO: This must match the templatedesigner default
    fontSizeCss() { return `${this.fontSize}pt`; },
    fontFamilyCss() {
      return this.fontName ? `"${this.fontName}", sans-serif` : 'sans-serif';
    },
    lineHeightCss() {
      return this.lineHeight ? this.lineHeight : 1.2; // 1.2 is default, TODO: This must match the templatedesigner default
    },
    textAlignCss() {
      if (this.storedAlignmentX === directions.LEFT) { return 'left'; }
      if (this.storedAlignmentX === directions.CENTER) { return 'center'; }
      if (this.storedAlignmentX === directions.RIGHT) { return 'right'; }
      return 'left';
    },
    parentStyle() {
      return {
        justifyContent: this.justifyContentStyle, // x-alignment
        alignItems: this.alignItemsStyle, // y-alignment
      };
    },
    isInternetExplorer() {
      return browserCheck.isInternetExplorer();
    },
    /**
     * x-alignment of text-parent (flexbox)
     */
    justifyContentStyle() {
      switch (this.storedAlignmentX) {
        case directions.LEFT:
          return 'flex-start';
        case directions.CENTER:
          return 'center';
        case directions.RIGHT:
          return 'flex-end';
        default:
          return 'flex-start'; // TODO: This should be put into a shared standards file
      }
    },
    /**
     * y-alignment of text-parent (flexbox)
     */
    alignItemsStyle() {
      switch (this.storedAlignmentY) {
        case directions.TOP:
          return 'flex-start';
        case directions.CENTER:
          return 'center';
        case directions.BOTTOM:
          return 'flex-end';
        default:
          return 'flex-start'; // TODO: This should be put into a shared standards file
      }
    },
    showTooltip() {
      return this.TOOLTIPS_VISIBLE && this.LAYER_IS_FIRST_OF_TYPE(this.layer.id, this.layer.type);
    },
    tooltipText: () => tooltipTexts.textLayer,
  },
  methods: {
    ...mapMutations([
      SET_TEXT_FOR_ACTIVE_LAYER,
      SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT,
    ]),
    ...mapActions([
      ACTIVATE_LAYER_BY_ID,
    ]),
    /* eslint-disable func-names */
    textEdited: debounce(async function (ev) {
      // It can sometimes happen, that text inside the layer is edited without the
      // layer being active (e.g. when the text is marked, without clicking it first).
      // So we need to make sure the layer is set to active before we commit the text.
      if (!this.layerIsActive) {
        log.debug('There is no active layer, so activating layer');
        await this.ACTIVATE_LAYER_BY_ID(this.layer.id);
        this.SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT();
      }
      let el = ev.target;
      // IE11-specific: the element sending the event might be a sub-element
      // of the contenteditable container. We need to find the parent then,
      // because we call innerHTML on it. We need the whole content.
      if (!el.classList.contains('kd-text-layer__text')) {
        // try to go up to 10 levels up. If we didn’t find the
        // contenteditable container by then something is wrong!
        const NUM_TRIES = 10;
        for (let i = 0; i < NUM_TRIES; i += 1) {
          el = el.parentElement;
          if (el && el.classList.contains('kd-text-layer__text')) { break; }
          if (i === NUM_TRIES - 1) {
            log.error('Could not find the contenteditable parent element');
            return;
          }
        }
      }

      const newTextAsHtml = el.innerHTML;
      this.SET_TEXT_FOR_ACTIVE_LAYER(newTextAsHtml);
      this.checkOverflowing();
    }, 50),
    /**
     * ATTENTION: This lead to a crash when template was edited in the past,
     * should be fixed now, but if there are weird issues this might be the reason.
     */
    checkOverflowing() {
      // A note on IE11:
      // When "textinput" event listener is used on the contenteditable-element
      // this function will be called whenever text is changed. The function below
      // (this.checkOverflowing()) results in the text being cleared. 
      // if (this.isInternetExplorer) { return; } 
      // correcting the font size only works when there is an active layer,
      // without this check it will fail, TODO: SET_FONT_SIZE_FOR_ACTIVE_LAYER
      // might need to be replaced with a call to the specific layer ID instead!
      if (!this.ACTIVE_LAYER_ID) { return; }
      if (!this.$refs || !this.$refs.text) {
        log.warn('this.$refs.text is not defined');
        return;
      }
      this.textIsOverflowing = this.elementIsOverflowing(this.$refs.text);
      if (this.textIsOverflowing) {
        if (this.fontSize <= 2) { return; }
        this.fontSize -= 2;
        this.$store.commit(SET_FONT_SIZE_FOR_ACTIVE_LAYER, this.fontSize);
        setTimeout(function() { this.placeCaretAtEnd(this.$refs.text); }.bind(this), 50);
        setTimeout(this.checkOverflowing.bind(this), 500);
      }
    },
    /**
     * Places the text caret at the end of the contenteditable-element.
     * Needed because some browsers set the cursor at the beginning when the text
     * has been auto-resized (to fit into the text layer).
     * Taken from https://stackoverflow.com/questions/45821199/set-caret-to-end-in-contenteditable-div-of-after-the-dealine
     */

    placeCaretAtEnd(el) {
      el.focus();
      if (typeof window.getSelection !== 'undefined' &&
        typeof document.createRange !== 'undefined') {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (typeof document.body.createTextRange !== 'undefined') {
        const textRange = document.body.createTextRange();
        textRange.moveToElementText(el);
        textRange.collapse(false);
        textRange.select();
      }
    },
    updateValues() {
      if (this.layer) {
        this.text = this.layer.text;
        this.fontSize = this.layer.fontSize;
        this.fontName = this.layer.fontName;
        this.fontWeight = this.layer.fontWeight;
        this.fontStyle = this.layer.fontStyle;
        this.lineHeight = this.layer.lineHeight ? this.layer.lineHeight : 1.2; // TODO: This must match the templatedesigner default
        this.color = this.layer.color;
        // TODO: Move default to other file
        this.alignmentX = this.layer.alignmentX || directions.LEFT;
        // TODO: Move default to other file
        this.alignmentY = this.layer.alignmentY || directions.LEFT;
        // setting the current layer only works when there is an active layer,
        // without this check it will fail
        if (this.ACTIVE_LAYER_ID) {
          setTimeout(this.checkOverflowing.bind(this), 500);
        }
      }
    },
    // Checks if element is too big, reference: parent element
    elementIsOverflowing(element) {
      if (!element || !element.parentNode) { return false; }
      return (element.scrollHeight > element.parentNode.clientHeight)
        || (element.scrollWidth > element.parentNode.clientWidth);
    },

    /**
     * Called when the outer text-wrapper (the visible text field) was clicked.
     * Sets the focus to the editable child textfield, which is needed when the
     * actual textfield (contenteditable element) is very small, because of
     * lack of content (no content or just one word).
     */
    onWrapperClick() {
      this.$refs.text.focus();
    },

    /**
     * Clipboard paste interceptor to get rid of rich text
     * and paste as plain text.
     * Code adapted from:
     * https://stackoverflow.com/a/48502897/1052107
     */
    onPaste(ev) {
      ev.preventDefault();
      const text = ev.clipboardData ? ev.clipboardData.getData('text/plain') : window.clipboardData.getData('Text');
      this.insertText(text);
    },

    /**
     * Helper function for the onPaste function (clipboard paste interceptor).
     * Pastes the passed text in the active text field and takes
     * care of replacing the current text selection (if any).
     * Code adapted from:
     * https://stackoverflow.com/a/48502897/1052107
     * @param {string} text - the text to paste
     */
    insertText(text) { 
      // use insertText command if supported
      if (document.queryCommandSupported('insertText')) {
        document.execCommand('insertText', false, text);
      }
      // or insert the text content at the caret's current position
      // replacing eventually selected content
      else {
        var range = document.getSelection().getRangeAt(0);
        range.deleteContents();
        var textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.selectNodeContents(textNode);
        range.collapse(false);

        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
  },
  watch: {
    storedText() {
      // when the user is entering text right now we don't want to overwrite
      // the component text valie
      const textIsBeingEntered = document.activeElement.classList
        .contains('kd-text-layer__text');
      if (textIsBeingEntered) { return; }
      this.text = this.storedText;
    },
    storedFontSize() { this.fontSize = this.storedFontSize; },
    storedFontName() { this.fontName = this.storedFontName; },
    storedFontWeight() { this.fontWeight = this.storedFontWeight; },
    storedFontStyle() { this.fontStyle = this.storedFontStyle; },
    storedLineHeight() { this.lineHeight = this.storedLineHeight; },
    storedColor() { this.color = this.storedColor; },
  },
  mounted() { this.updateValues(); },
  updated() { this.updateValues(); },
};
</script>

<style lang="scss">

.kd-text-layer {
  width: 100%;
  height: 100%;
  /*
   * setting overflow hidden on the text-wrapper causes the text decenders
   * e.g. the letter "g" to be cut off when the text is aligned at the bottom.
   * The only fixes seem to be disabling overflow: hidden here, or setting the
   * "line-height" to "normal", which is not aesthetically pleasing in many cases.
   */
  // overflow: hidden;
  display: flex;
}

.kd-text-layer__text {
  // width: 100%;
  // height: 100%;
}

.kd-text-layer__wrapper {
  width: 100%;
  height: 100%;
}
</style>
